import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle, CalendarBlank } from "@phosphor-icons/react";
import { format } from "date-fns";
import { toast } from "sonner";
import api, { fileUrl, formatINR, formatApiError } from "@/lib/api";
import { track } from "@/lib/site";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/Loaders";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BOOKING_TYPES = ["Day Out", "Overnight Stay", "Event/Party", "Function"];
const priceKey = { "Day Out": "dayout", "Overnight Stay": "overnight", "Event/Party": "weekend", Function: "weekend" };
const PACKAGES = ["Standard", "Premium", "Deluxe"];

export default function Booking() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [prop, setProp] = useState(null);
  const [date, setDate] = useState(null);
  const [guests, setGuests] = useState(2);
  const [bookingType, setBookingType] = useState(params.get("type") || "Day Out");
  const [pkg, setPkg] = useState("Standard");
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [unavailable, setUnavailable] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [couponInfo, setCouponInfo] = useState(null);

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const r = await api.post("/coupons/validate", { code: coupon, property_id: id, booking_type: bookingType });
      setCouponInfo(r.data);
      if (r.data.valid) { toast.success(`Coupon applied — you save ${formatINR(r.data.discount)}`); track("coupon_apply", { code: r.data.code }); }
      else toast.error(r.data.reason);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const changeType = (t) => { setBookingType(t); setCouponInfo(null); };

  useEffect(() => {
    api.get(`/properties/${id}`).then((r) => setProp(r.data));
    api.get(`/properties/${id}/availability`).then((r) => {
      setUnavailable(r.data.dates.filter((d) => d.status !== "pending").map((d) => d.date));
    });
    track("booking_start", { property_id: id });
    api.post("/bookings/abandoned", { property_id: id, last_step: "opened_booking" }).catch(() => {});
  }, [id]);

  if (!prop) return <div className="flex h-screen items-center justify-center"><Spinner /></div>;

  const base = prop.prices[priceKey[bookingType]] || prop.prices.starting;
  const taxes = Math.round(base * 0.05);
  const total = base + taxes;
  const dateStr = date ? format(date, "dd/MM/yyyy") : "";
  const isUnavailable = dateStr && unavailable.includes(dateStr);

  const submit = async () => {
    if (!date) return toast.error("Please select a date.");
    if (!name || !phone) return toast.error("Please fill your details.");
    if (isUnavailable) return toast.error("This property is no longer available for the selected date. Please choose another date.");
    setSubmitting(true);
    try {
      const r = await api.post("/bookings", {
        property_id: id, date: dateStr, guests, booking_type: bookingType, package: pkg, customer_name: name, customer_phone: phone,
        coupon_code: couponInfo?.valid ? coupon.trim().toUpperCase() : "",
      });
      setSuccess(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
        <div className="animate-fade-up">
          <CheckCircle size={80} weight="fill" className="mx-auto text-primary" />
          <h1 className="mt-5 font-heading text-2xl font-bold text-stone-900">Booking request submitted successfully.</h1>
          <p className="mt-2 text-sm text-stone-500">Your request is pending owner confirmation.</p>
          <div className="mt-6 rounded-2xl border border-stone-200 bg-white p-5 text-left">
            <div className="flex justify-between border-b border-stone-100 py-2"><span className="text-stone-500">Booking ID</span><span data-testid="booking-ref" className="font-mono font-semibold">{success.booking_ref}</span></div>
            <div className="flex justify-between border-b border-stone-100 py-2"><span className="text-stone-500">Property</span><span className="font-medium">{success.property_name}</span></div>
            <div className="flex justify-between border-b border-stone-100 py-2"><span className="text-stone-500">Date</span><span className="font-medium">{success.date}</span></div>
            <div className="flex justify-between border-b border-stone-100 py-2"><span className="text-stone-500">Total</span><span className="font-semibold">{formatINR(success.total_amount)}</span></div>
            <div className="flex justify-between py-2"><span className="text-stone-500">Status</span><span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">Pending</span></div>
          </div>
          <div className="mt-6 flex gap-3">
            <Button variant="outline" className="flex-1 rounded-xl" onClick={() => navigate("/")}>Home</Button>
            <Button data-testid="view-bookings-btn" className="flex-1 rounded-xl bg-primary hover:bg-green-900" onClick={() => navigate("/bookings")}>My Bookings</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 pb-32 pt-5">
      <button data-testid="booking-back" onClick={() => navigate(-1)} className="mb-3 flex items-center gap-1 text-sm text-stone-600"><ArrowLeft size={18} /> Back</button>
      <h1 className="font-heading text-2xl font-semibold text-stone-900">Confirm your booking</h1>

      <div className="mt-4 flex items-center gap-3 rounded-2xl border border-stone-200 bg-white p-3">
        <img src={fileUrl(prop.images?.[0])} alt={prop.name} className="h-16 w-16 rounded-xl object-cover" />
        <div>
          <p className="font-heading font-semibold text-stone-900">{prop.name}</p>
          <p className="text-sm text-stone-500">{prop.location}</p>
        </div>
      </div>

      <div className="mt-5 space-y-4">
        <div>
          <Label>Booking type</Label>
          <div className="no-scrollbar mt-2 flex gap-2 overflow-x-auto">
            {BOOKING_TYPES.map((t) => (
              <button key={t} data-testid={`booking-type-${t}`} onClick={() => changeType(t)} className={`shrink-0 rounded-full px-4 py-2 text-sm ${bookingType === t ? "bg-primary text-white" : "bg-stone-100 text-stone-700"}`}>{t}</button>
            ))}
          </div>
        </div>

        <div>
          <Label>Date (DD/MM/YYYY)</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button data-testid="date-picker" variant="outline" className="mt-1 w-full justify-start gap-2 rounded-xl font-normal">
                <CalendarBlank size={18} /> {dateStr || "Select date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0)) || unavailable.includes(format(d, "dd/MM/yyyy"))}
              />
            </PopoverContent>
          </Popover>
          {isUnavailable && <p className="mt-1 text-sm text-destructive">This date is unavailable. Please choose another.</p>}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Guests</Label>
            <Input data-testid="guests-input" type="number" min={1} max={prop.max_guests} value={guests} onChange={(e) => setGuests(Number(e.target.value))} className="mt-1 rounded-xl" />
          </div>
          <div>
            <Label>Package</Label>
            <Select value={pkg} onValueChange={setPkg}>
              <SelectTrigger data-testid="package-select" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{PACKAGES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Your name</Label>
            <Input data-testid="cust-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 rounded-xl" />
          </div>
          <div>
            <Label>Phone</Label>
            <Input data-testid="cust-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1 rounded-xl" />
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-stone-200 bg-white p-5">
        <h2 className="font-heading text-lg font-semibold">Have a coupon?</h2>
        <div className="mt-3 flex gap-2">
          <Input data-testid="coupon-input" value={coupon} onChange={(e) => { setCoupon(e.target.value.toUpperCase()); setCouponInfo(null); }} placeholder="Enter code e.g. WELCOME10" className="rounded-xl uppercase" />
          <Button data-testid="apply-coupon" type="button" variant="outline" onClick={applyCoupon} className="rounded-xl">Apply</Button>
        </div>
        {couponInfo && (
          <p data-testid="coupon-result" className={`mt-2 text-sm ${couponInfo.valid ? "text-primary" : "text-destructive"}`}>
            {couponInfo.valid ? `Coupon ${couponInfo.code} applied — you save ${formatINR(couponInfo.discount)}.` : couponInfo.reason}
          </p>
        )}
      </div>

      <div className="mt-4 rounded-2xl border border-stone-200 bg-white p-5">
        <h2 className="font-heading text-lg font-semibold">Booking Summary</h2>
        <div className="mt-3 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-stone-500">Base price</span><span className="font-medium tabular-nums">{formatINR(base)}</span></div>
          <div className="flex justify-between"><span className="text-stone-500">Taxes & fees (5%)</span><span className="font-medium tabular-nums">{formatINR(taxes)}</span></div>
          {couponInfo?.valid && <div className="flex justify-between text-primary"><span>Coupon {couponInfo.code}</span><span className="font-medium tabular-nums">- {formatINR(couponInfo.discount)}</span></div>}
          <div className="mt-2 flex justify-between border-t border-stone-100 pt-3"><span className="font-heading font-semibold">Total amount</span><span data-testid="booking-total" className="font-heading text-lg font-bold text-primary tabular-nums">{formatINR(couponInfo?.valid ? total - couponInfo.discount : total)}</span></div>
          <p className="pt-2 text-xs text-stone-400">No payment required now. Pay after owner confirms. Final amount is calculated securely by Avinya.</p>
        </div>
      </div>

      <div className="fixed bottom-0 left-1/2 z-40 w-full max-w-lg -translate-x-1/2 border-t border-stone-200 bg-white/90 px-4 pt-3 pb-safe-3 backdrop-blur-xl md:max-w-2xl">
        <Button data-testid="submit-booking" onClick={submit} disabled={submitting} className="w-full rounded-full bg-primary py-6 text-base hover:bg-green-900 active:scale-95">
          {submitting ? "Submitting..." : `Submit Booking Request · ${formatINR(couponInfo?.valid ? total - couponInfo.discount : total)}`}
        </Button>
      </div>
    </div>
  );
}
